import { useState } from 'react'
import { MoreHorizontal, UserPlus } from 'lucide-react'
import { Avatar, Badge, Button, Card, CardHeader, Dropdown, IconButton, Table, type Column } from '@/components/ui'
import { QueryBoundary } from '@/components/domain'
import { PERMISSIONS, useRoles, usePermissions } from '@/features/authorization'
import { formatRelative } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import { useSessionStore } from '@/stores'
import type { User } from '@/types'
import { InviteMemberModal } from '../components/InviteMemberModal'
import { useInviteMember, useMembers, useUpdateMember } from '../hooks'

const STATUS_KEY: Record<User['status'], string> = {
  active: 'members.status.active',
  invited: 'members.status.invited',
  suspended: 'members.status.suspended',
}

export function MembersSettings() {
  const t = useT()
  const { data, isPending, error, refetch } = useMembers()
  // The workspace's OWN roles, not a hardcoded four: "admin" may be called
  // "Operations" here, and there may be a "Patron Lead" that no constant knows.
  const roles = useRoles()
  const { has } = usePermissions()
  const update = useUpdateMember()
  const invite = useInviteMember()
  const myUserId = useSessionStore((s) => s.user?.id)
  const [inviteOpen, setInviteOpen] = useState(false)

  const canManage = has(PERMISSIONS.memberManage)
  const roleName = (key: string) =>
    roles.data?.find((role) => role.key === key)?.name ?? key

  const columns: Column<User>[] = [
    {
      id: 'name',
      header: t('members.col.member'),
      cell: (row) => (
        <span className="flex items-center gap-2.5">
          <Avatar name={row.name} src={row.avatarUrl} size="sm" useSpeakerColor />
          <span className="min-w-0">
            <span className="block truncate font-medium text-fg">{row.name}</span>
            <span className="block truncate text-sm text-fg-subtle">{row.email}</span>
          </span>
        </span>
      ),
    },
    {
      id: 'role',
      header: t('members.col.role'),
      width: 'w-32',
      cell: (row) => roleName(String(row.role)),
    },
    {
      id: 'status',
      header: t('members.col.status'),
      width: 'w-28',
      cell: (row) => (
        <Badge tone={row.status === 'active' ? 'success' : row.status === 'invited' ? 'info' : 'danger'}>
          {t(STATUS_KEY[row.status])}
        </Badge>
      ),
    },
    {
      id: 'lastActive',
      header: t('members.col.lastActive'),
      hideOnMobile: true,
      width: 'w-36',
      cell: (row) => (
        <span className="text-fg-muted">{row.lastActiveAt ? formatRelative(row.lastActiveAt) : t('common.never')}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      width: 'w-12',
      cell: (row) => {
        // NOBODY CHANGES THEIR OWN ROLE. The API refuses it and the trigger
        // refuses it again; disabling the items here means the person is not
        // invited to try. Without the rule, an admin whose role was
        // deliberately built without meeting access could assign themselves
        // Member and take that access straight back.
        const isSelf = row.id === myUserId
        const roleItems = (roles.data ?? [])
          .filter((role) => role.key !== row.role)
          .map((role) => ({
            id: `role-${role.id}`,
            label: t('members.changeTo', { role: role.name }),
            // A protected role is grantable only by somebody who holds it, and
            // `canEdit` is the server's answer to exactly that question.
            disabled: !canManage || isSelf || (role.isProtected && !role.canEdit),
            onSelect: () =>
              update.mutate({ id: row.id, patch: { role: role.key } }),
          }))

        return (
          <Dropdown
            trigger={({ toggle }) => (
              <IconButton label={t('members.manage', { name: row.name })} icon={<MoreHorizontal />} size="sm" onClick={toggle} />
            )}
            items={[
              ...roleItems,
              {
                id: 'resend',
                label: t('members.resendInvitation'),
                separatorBefore: roleItems.length > 0,
                // Re-inviting the same address is the resend: the store
                // revokes the still-pending invitation and issues a fresh one
                // with a new expiry (see `postgres_workspace.invite_member`),
                // so there is no separate resend endpoint to call.
                disabled: row.status !== 'invited' || invite.isPending,
                onSelect: () => invite.mutate({ email: row.email, role: row.role }),
              },
              {
                id: 'suspend',
                label: t('members.removeFromWorkspace'),
                destructive: true,
                separatorBefore: true,
                disabled: !canManage || isSelf,
                onSelect: () =>
                  update.mutate({ id: row.id, patch: { status: 'suspended' } }),
              },
            ]}
          />
        )
      },
    },
  ]

  return (
    <Card>
      <CardHeader
        title={t('members.title')}
        description={t('members.description')}
        action={
          canManage ? (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<UserPlus className="h-4 w-4" />}
              onClick={() => setInviteOpen(true)}
            >
              {t('members.invitePeople')}
            </Button>
          ) : undefined
        }
      />
      <QueryBoundary isLoading={isPending} error={error} onRetry={refetch}>
        <Table columns={columns} rows={data ?? []} rowKey={(row) => row.id} />
      </QueryBoundary>
      {canManage && <InviteMemberModal isOpen={inviteOpen} onClose={() => setInviteOpen(false)} />}
    </Card>
  )
}
